import React, {
  useState,
  useEffect,
  useReducer,
  useCallback,
  useContext,
} from "react";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import SearchIcon from "@material-ui/icons/Search";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import LockIcon from "@material-ui/icons/Lock";
import VisibilityIcon from "@material-ui/icons/Visibility";
import VisibilityOffIcon from "@material-ui/icons/VisibilityOff";
import AddIcon from "@material-ui/icons/Add";
import FolderIcon from "@material-ui/icons/Folder";
import FolderOpenIcon from "@material-ui/icons/FolderOpen";
import Switch from "@material-ui/core/Switch";
import Tooltip from "@material-ui/core/Tooltip";
import Collapse from "@material-ui/core/Collapse";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";

import { deleteTag } from "../../services/tags.api";
import {
  listTagGroups,
  createTagGroup,
  renameTagGroup,
  toggleTagGroupKanban,
  deleteTagGroup,
  saveKanbanColumnPref,
  reorderKanbanColumns,
} from "../../services/tagGroups.api";
// Mismo motor de arrastre que usa el tablero: el orden que se define aquí es el que se
// verá allí, así que conviene que el gesto se sienta igual en ambos sitios.
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import DragIndicatorIcon from "@material-ui/icons/DragIndicator";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import TagModal from "../../components/TagModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import { Chip } from "@material-ui/core";
import { SocketContext } from "../../context/Socket/SocketContext";
import { AuthContext } from "../../context/Auth/AuthContext";

// ── Reducer ────────────────────────────────────────────────────────────────
const reducer = (state, action) => {
  switch (action.type) {
    case "LOAD_GROUPS":
      return action.payload;

    case "ADD_GROUP":
      return [...state, { ...action.payload, tags: [] }];

    case "UPDATE_GROUP":
      return state.map(g =>
        g.id === action.payload.groupId
          ? { ...g, ...action.payload }
          : g
      );

    case "TOGGLE_KANBAN":
      return state.map(g => ({
        ...g,
        kanbanActive: g.id === action.payload
      }));

    case "DELETE_GROUP":
      return state.filter(g => g.id !== action.payload);

    case "UPDATE_TAG_IN_GROUP":
      return state.map(g => ({
        ...g,
        tags: g.tags.map(t => t.id === action.payload.id ? action.payload : t)
      }));

    case "DELETE_TAG_IN_GROUP":
      return state.map(g => ({
        ...g,
        tags: g.tags.filter(t => t.id !== action.payload)
      }));

    default:
      return state;
  }
};

// ── Styles ─────────────────────────────────────────────────────────────────
const useStyles = makeStyles(theme => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  groupCard: {
    marginBottom: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(1),
    overflow: "hidden",
  },
  /**
   * Calibración oscura (0391 · deuda DT-7).
   *
   * `theme.palette.grey[100]` es casi blanco y no conmuta con el modo: en oscuro las
   * cabeceras de grupo salían como bandas claras sobre el resto de la pantalla. Se
   * resuelve por modo, con la misma familia que usa el resto de la aplicación.
   */
  groupHeader: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(1.5, 2),
    backgroundColor: theme.palette.type === "dark" ? "#1f2937" : theme.palette.grey[100],
    cursor: "pointer",
    userSelect: "none",
    "&:hover": {
      backgroundColor: theme.palette.type === "dark" ? "#273246" : theme.palette.grey[200],
    },
  },
  groupHeaderDark: {
    backgroundColor: theme.palette.type === "dark" ? "#111827" : theme.palette.grey[100],
  },
  groupName: {
    fontWeight: 700,
    fontSize: "0.95rem",
    flexGrow: 1,
    marginLeft: theme.spacing(1),
  },
  kanbanSwitch: {
    marginLeft: "auto",
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
  },
  /**
   * La cabecera de tabla era `#1976d2` fijo con texto blanco. Sobre superficie oscura ese
   * azul saturado no informa, compite: es lo más brillante de la pantalla y arrastra la
   * mirada por encima de los datos. En oscuro pasa a superficie hundida con texto
   * secundario; en claro se conserva el azul de siempre.
   */
  tableHeader: {
    "& .MuiTableCell-head": {
      backgroundColor: theme.palette.type === "dark" ? "#111827" : "#1976d2",
      color: theme.palette.type === "dark" ? theme.palette.text.secondary : "white",
      borderBottom: `1px solid ${theme.palette.divider}`,
      fontWeight: "bold",
      fontSize: "0.8rem",
    },
  },
  tagChip: {
    fontWeight: "bold",
    fontSize: "0.72rem",
    minWidth: "70px",
    textShadow: "1px 1px 1px rgba(0,0,0,0.3)",
  },
  originChip: {
    fontSize: "0.65rem",
    fontWeight: "bold",
  },
  groupActions: {
    display: "flex",
    alignItems: "center",
    gap: 4,
  },
  addTagBtn: {
    margin: theme.spacing(1, 2, 1),
    fontSize: "0.75rem",
  },
}));

// ── Componente principal ───────────────────────────────────────────────────
/**
 * Celdas de una fila de etiqueta.
 *
 * `isSystem` decide QUÉ ACCIONES se ofrecen (renombrar/ocultar el carril, o editar/borrar
 * la etiqueta). El asa de arrastre no depende de eso: aparece siempre que la fila sea
 * arrastrable, y eso lo decide quien la renderiza.
 */
function TagCells({
  tag,
  isSystem,
  classes,
  dragHandleProps,
  onRenameColumn,
  onToggleColumnVisible,
  onEditTag,
  onAskDeleteTag,
}) {
  return (
    <>
      <TableCell align="center">
        <Box display="flex" alignItems="center" justifyContent="center" style={{ gap: 4 }}>
          {dragHandleProps && (
            <span {...dragHandleProps} style={{ display: "flex", cursor: "grab" }}>
              <DragIndicatorIcon fontSize="small" style={{ opacity: 0.45 }} />
            </span>
          )}
          <Chip
            className={classes.tagChip}
            style={{ backgroundColor: tag.color, color: "white" }}
            label={tag.name}
            size="small"
          />
        </Box>
      </TableCell>
      <TableCell align="center">
        <strong>{tag.ticketsCount || 0}</strong>
      </TableCell>
      <TableCell align="center">
        <Chip
          label={tag.kanban === 1 ? i18n.t("tags.kanbanActive") : i18n.t("tags.kanbanInactive")}
          size="small"
          style={{
            backgroundColor: tag.kanban === 1 ? "#4caf50" : "#9e9e9e",
            color: "white",
            fontWeight: "bold",
          }}
        />
      </TableCell>
      <TableCell align="center">
        {isSystem ? (
          /* Los carriles del tablero se pueden renombrar y ocultar. No se ofrece borrar:
             el motor cuenta con que el carril exista, y ocultar cubre el caso sin perder
             las tarjetas que hubiera dentro. */
          <>
            <IconButton
              size="small"
              onClick={() => onRenameColumn(tag)}
              title={i18n.t("tags.tooltips.renameColumn")}
            >
              <EditIcon fontSize="small" style={{ color: "#1976d2" }} />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => onToggleColumnVisible(tag)}
              title={
                tag.isVisible === 0
                  ? i18n.t("tags.tooltips.showColumn")
                  : i18n.t("tags.tooltips.hideColumn")
              }
            >
              {tag.isVisible === 0 ? (
                <VisibilityOffIcon fontSize="small" style={{ color: "#f57c00" }} />
              ) : (
                <VisibilityIcon fontSize="small" style={{ color: "#4caf50" }} />
              )}
            </IconButton>
          </>
        ) : (
          <>
            <IconButton
              size="small"
              onClick={() => onEditTag(tag)}
              title={i18n.t("tags.tooltips.editTag")}
            >
              <EditIcon fontSize="small" style={{ color: "#1976d2" }} />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => onAskDeleteTag(tag)}
              title={i18n.t("tags.tooltips.deleteTag")}
            >
              <DeleteOutlineIcon fontSize="small" style={{ color: "#f44336" }} />
            </IconButton>
          </>
        )}
      </TableCell>
    </>
  );
}

/**
 * Cuerpo de la tabla de etiquetas.
 *
 * TODOS los grupos se arrastran para reordenar: el orden definido aquí es el que se ve en
 * el tablero. Antes solo lo permitía el grupo de sistema, con el argumento de que en los
 * manuales «las etiquetas no son columnas de un embudo» — y es falso: en cuanto el tablero
 * apunta a un grupo manual, sus etiquetas SON las columnas. El efecto era además al revés
 * de lo útil: el grupo de sistema, inactivo, mostraba las asas, y el que se estaba usando
 * no.
 *
 * `DragDropContext` y `Droppable` no generan DOM propio, así que el árbol resultante sigue
 * siendo `<Table><TableBody>` y la tabla no se rompe.
 */
function TagsTableBody({ tags, isSystem, groupId, classes, onDragEnd, ...cellProps }) {
  const hiddenStyle = (tag) => (tag.isVisible === 0 ? { opacity: 0.45 } : undefined);

  return (
    <DragDropContext onDragEnd={(result) => onDragEnd(result, tags)}>
      <Droppable droppableId={`kanban-columns-${groupId}`}>
        {(dropProvided) => (
          <TableBody ref={dropProvided.innerRef} {...dropProvided.droppableProps}>
            {tags.map((tag, index) => (
              <Draggable key={tag.id} draggableId={String(tag.id)} index={index}>
                {(dragProvided, snapshot) => (
                  <TableRow
                    hover
                    ref={dragProvided.innerRef}
                    {...dragProvided.draggableProps}
                    style={{
                      ...hiddenStyle(tag),
                      ...dragProvided.draggableProps.style,
                      // Al arrastrar, la fila sale del flujo de la tabla y pierde el ancho
                      // de sus columnas; mostrarla como bloque evita que se colapse.
                      display: snapshot.isDragging ? "table" : undefined,
                      background: snapshot.isDragging ? "rgba(25,118,210,0.12)" : undefined,
                    }}
                  >
                    <TagCells
                      tag={tag}
                      isSystem={isSystem}
                      classes={classes}
                      dragHandleProps={dragProvided.dragHandleProps}
                      {...cellProps}
                    />
                  </TableRow>
                )}
              </Draggable>
            ))}
            {dropProvided.placeholder}
          </TableBody>
        )}
      </Droppable>
    </DragDropContext>
  );
}

const Tags = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const socketManager = useContext(SocketContext);

  const [groups, dispatch] = useReducer(reducer, []);
  const [loading, setLoading] = useState(false);
  const [searchParam, setSearchParam] = useState("");

  // Tag modal
  const [tagModalOpen, setTagModalOpen] = useState(false);
  const [selectedTag, setSelectedTag] = useState(null);
  const [selectedGroupId, setSelectedGroupId] = useState(null);

  // Confirm modals
  const [deletingTag, setDeletingTag] = useState(null);
  const [tagConfirmOpen, setTagConfirmOpen] = useState(false);
  const [deletingGroup, setDeletingGroup] = useState(null);
  const [groupConfirmOpen, setGroupConfirmOpen] = useState(false);

  // New group dialog
  // 0391: renombrado de carril con el modal del producto, no con window.prompt
  const [renamingColumn, setRenamingColumn] = useState(null);
  const [renamingColumnName, setRenamingColumnName] = useState("");
  const [newGroupDialogOpen, setNewGroupDialogOpen] = useState(false);
  const [newGroupName, setNewGroupName] = useState("");
  const [newGroupLoading, setNewGroupLoading] = useState(false);

  // Rename group dialog
  const [renamingGroup, setRenamingGroup] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [renameLoading, setRenameLoading] = useState(false);

  // Collapse state per group
  const [collapsed, setCollapsed] = useState({});

  // ── Fetch ────────────────────────────────────────────────────────────────
  const fetchGroups = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await listTagGroups();
      dispatch({ type: "LOAD_GROUPS", payload: data });
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchGroups(); }, [fetchGroups]);

  // ── Socket ───────────────────────────────────────────────────────────────
  useEffect(() => {
    const socket = socketManager.getSocket(user.companyId);

    const handleTagGroup = (data) => {
      if (data.action === "create") {
        dispatch({ type: "ADD_GROUP", payload: data.group });
      } else if (data.action === "delete") {
        dispatch({ type: "DELETE_GROUP", payload: data.groupId });
        fetchGroups();
      } else if (data.action === "toggleKanban") {
        dispatch({ type: "TOGGLE_KANBAN", payload: data.groupId });
      } else if (data.action === "update") {
        dispatch({ type: "UPDATE_GROUP", payload: data });
      }
    };

    const handleTag = (data) => {
      if (data.action === "update" || data.action === "create") {
        fetchGroups();
      } else if (data.action === "delete") {
        dispatch({ type: "DELETE_TAG_IN_GROUP", payload: +data.tagId });
      }
    };

    socket.on(`company-${user.companyId}-taggroup`, handleTagGroup);
    socket.on(`company-${user.companyId}-tag`, handleTag);

    return () => {
      socket.off(`company-${user.companyId}-taggroup`, handleTagGroup);
      socket.off(`company-${user.companyId}-tag`, handleTag);
    };
  }, [socketManager, user, fetchGroups]);

  // ── Handlers Grupo ────────────────────────────────────────────────────────
  const handleToggleKanban = async (group) => {
    if (user.profile !== "admin") return;
    try {
      await toggleTagGroupKanban(group.id);
      dispatch({ type: "TOGGLE_KANBAN", payload: group.id });
    } catch (err) {
      toastError(err);
    }
  };

  const handleCreateGroup = async () => {
    if (!newGroupName.trim()) return;
    try {
      setNewGroupLoading(true);
      await createTagGroup({ name: newGroupName.trim() });
      setNewGroupDialogOpen(false);
      setNewGroupName("");
    } catch (err) {
      toastError(err);
    } finally {
      setNewGroupLoading(false);
    }
  };

  const handleRenameGroup = async () => {
    if (!renameValue.trim() || !renamingGroup) return;
    try {
      setRenameLoading(true);
      await renameTagGroup(renamingGroup.id, { name: renameValue.trim() });
      dispatch({ type: "UPDATE_GROUP", payload: { groupId: renamingGroup.id, name: renameValue.trim() } });
      setRenamingGroup(null);
      setRenameValue("");
    } catch (err) {
      toastError(err);
    } finally {
      setRenameLoading(false);
    }
  };

  const handleDeleteGroup = async (groupId) => {
    try {
      await deleteTagGroup(groupId);
      toast.success("Grupo eliminado");
    } catch (err) {
      toastError(err);
    }
    setDeletingGroup(null);
  };

  // ── Handlers Tag ──────────────────────────────────────────────────────────
  const handleOpenTagModal = (groupId = null) => {
    setSelectedTag(null);
    setSelectedGroupId(groupId);
    setTagModalOpen(true);
  };

  const handleEditTag = (tag) => {
    setSelectedTag(tag);
    setSelectedGroupId(tag.groupId);
    setTagModalOpen(true);
  };

  /**
   * 0391 F4 — Personalización de los carriles del tablero de Agenda.
   *
   * Estas columnas estaban en "Solo lectura" porque su nombre ERA su identidad: al
   * emparejarse por texto, renombrarlas rompía el motor. Con `stageKey` (migración 325)
   * el nombre pasó a ser presentación, así que el candado ya no aplica.
   *
   * No se ofrece borrar: el motor cuenta con que el carril exista. Ocultar cubre el caso
   * real —"Sesion de Prueba" no se usa en un negocio 1:1— sin perder las tarjetas que
   * hubiera dentro.
   */
  /**
   * Renombrar carril mediante el modal del producto.
   *
   * WHY: la primera versión usaba `window.prompt`. Funciona, pero rompe con el resto de
   * la aplicación —tipografía del navegador, sin tema oscuro, sin traducción— y en algunos
   * navegadores el usuario puede silenciar esos diálogos, dejando la acción muerta sin
   * ningún aviso.
   */
  const handleRenameColumn = (tag) => {
    setRenamingColumn(tag);
    setRenamingColumnName(tag.name || "");
  };

  const handleConfirmRenameColumn = async () => {
    if (!renamingColumn) return;
    const nuevo = renamingColumnName.trim();
    if (!nuevo) {
      toast.error(i18n.t("tags.columnNameRequired"));
      return;
    }
    try {
      await saveKanbanColumnPref(renamingColumn.id, { name: nuevo });
      toast.success(i18n.t("tags.columnSaved"));
      setRenamingColumn(null);
      fetchGroups();
    } catch (err) {
      toastError(err);
    }
  };

  const handleToggleColumnVisible = async (tag) => {
    try {
      await saveKanbanColumnPref(tag.id, { isVisible: !(tag.isVisible ?? 1) });
      toast.success(i18n.t("tags.columnSaved"));
      fetchGroups();
    } catch (err) {
      toastError(err);
    }
  };

  /**
   * Reordenar carriles arrastrando. El orden definido aquí es el que se ve en el tablero.
   *
   * Se envía la lista completa en una sola llamada: mover una fila cambia la posición de
   * todas las demás, y hacerlo de una en una dejaría posiciones duplicadas a medio camino.
   */
  const handleColumnDragEnd = async (result, tags) => {
    if (!result.destination || result.destination.index === result.source.index) return;

    // Con un filtro activo la lista está recortada: enviar ese orden dejaría fuera a los
    // carriles no visibles y el resultado no sería el que el usuario ve.
    if (searchParam) {
      toast.info(i18n.t("tags.reorderNeedsNoFilter"));
      return;
    }

    const ordered = Array.from(tags);
    const [moved] = ordered.splice(result.source.index, 1);
    ordered.splice(result.destination.index, 0, moved);

    try {
      await reorderKanbanColumns(ordered.map((t) => t.id));
      toast.success(i18n.t("tags.columnsReordered"));
      fetchGroups();
    } catch (err) {
      toastError(err);
      fetchGroups(); // deshace el movimiento visual si el servidor lo rechazó
    }
  };

  const handleDeleteTag = async (tagId) => {
    try {
      await deleteTag(tagId);
      toast.success(i18n.t("tags.toasts.deleted"));
      dispatch({ type: "DELETE_TAG_IN_GROUP", payload: tagId });
    } catch (err) {
      toastError(err);
    }
    setDeletingTag(null);
  };

  // ── Filtrado ──────────────────────────────────────────────────────────────
  const filterTag = (tag) => {
    if (!searchParam) return true;
    return tag.name.toLowerCase().includes(searchParam.toLowerCase());
  };

  const toggleCollapse = (groupId) => {
    setCollapsed(prev => ({ ...prev, [groupId]: !prev[groupId] }));
  };

  const isAdmin = user.profile === "admin";

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <MainContainer>
      {/* Confirm: eliminar tag */}
      <ConfirmationModal
        title="Eliminar etiqueta"
        open={tagConfirmOpen}
        onClose={setTagConfirmOpen}
        onConfirm={() => handleDeleteTag(deletingTag?.id)}
      >
        ¿Estás seguro de eliminar esta etiqueta?
      </ConfirmationModal>

      {/* Confirm: eliminar grupo */}
      <ConfirmationModal
        title="Eliminar grupo"
        open={groupConfirmOpen}
        onClose={setGroupConfirmOpen}
        onConfirm={() => handleDeleteGroup(deletingGroup?.id)}
      >
        Las etiquetas de este grupo se moverán al grupo General.
      </ConfirmationModal>

      {/* Dialog: renombrar carril del tablero (0391 F4) */}
      <Dialog
        open={!!renamingColumn}
        onClose={() => setRenamingColumn(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{i18n.t("tags.columnRenameTitle")}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label={i18n.t("tags.columnRenamePrompt")}
            value={renamingColumnName}
            onChange={(e) => setRenamingColumnName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleConfirmRenameColumn()}
            variant="outlined"
            size="small"
            helperText={i18n.t("tags.columnRenameHelper")}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenamingColumn(null)}>
            {i18n.t("tags.buttons.cancel")}
          </Button>
          <Button
            onClick={handleConfirmRenameColumn}
            color="primary"
            variant="contained"
            disabled={!renamingColumnName.trim()}
          >
            {i18n.t("tags.buttons.save")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: nuevo grupo */}
      <Dialog open={newGroupDialogOpen} onClose={() => setNewGroupDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Nuevo grupo de etiquetas</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Nombre del grupo"
            value={newGroupName}
            onChange={e => setNewGroupName(e.target.value)}
            onKeyDown={e => e.key === "Enter" && handleCreateGroup()}
            variant="outlined"
            size="small"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewGroupDialogOpen(false)}>Cancelar</Button>
          <Button
            onClick={handleCreateGroup}
            color="primary"
            variant="contained"
            disabled={newGroupLoading || !newGroupName.trim()}
          >
            Crear
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: renombrar grupo */}
      <Dialog open={!!renamingGroup} onClose={() => setRenamingGroup(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Renombrar grupo</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Nuevo nombre"
            value={renameValue}
            onChange={e => setRenameValue(e.target.value)}
            onKeyDown={e => e.key === "Enter" && handleRenameGroup()}
            variant="outlined"
            size="small"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenamingGroup(null)}>Cancelar</Button>
          <Button
            onClick={handleRenameGroup}
            color="primary"
            variant="contained"
            disabled={renameLoading || !renameValue.trim()}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Tag modal */}
      <TagModal
        open={tagModalOpen}
        onClose={() => { setSelectedTag(null); setTagModalOpen(false); }}
        reload={fetchGroups}
        tagId={selectedTag?.id}
        initialGroupId={selectedGroupId}
      />

      {/* Header */}
      <MainHeader>
        <Title>{i18n.t("tags.title")}</Title>
        <MainHeaderButtonsWrapper>
          <TextField
            placeholder={i18n.t("contacts.searchPlaceholder")}
            type="search"
            value={searchParam}
            onChange={e => setSearchParam(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: "gray" }} />
                </InputAdornment>
              ),
            }}
          />
          {isAdmin && (
            <Button
              variant="outlined"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => { setNewGroupName(""); setNewGroupDialogOpen(true); }}
            >
              Nuevo grupo
            </Button>
          )}
          <Button
            variant="contained"
            color="primary"
            onClick={() => handleOpenTagModal(null)}
          >
            {i18n.t("tags.buttons.add")}
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined">
        {loading && groups.length === 0 && (
          <Table size="small">
            <TableBody><TableRowSkeleton columns={5} /></TableBody>
          </Table>
        )}

        {groups.map(group => {
          const visibleTags = group.tags.filter(filterTag);
          const isSystem = group.origin === "system";
          const isOpen = !collapsed[group.id];

          return (
            <Box key={group.id} className={classes.groupCard}>
              {/* Group header */}
              <Box
                className={classes.groupHeader}
                onClick={() => toggleCollapse(group.id)}
              >
                {isSystem
                  ? <LockIcon fontSize="small" color="disabled" />
                  : (isOpen ? <FolderOpenIcon fontSize="small" color="primary" /> : <FolderIcon fontSize="small" color="primary" />)
                }
                <Typography className={classes.groupName}>
                  {group.name}
                </Typography>

                <Chip
                  label={isSystem ? "Sistema" : "Manual"}
                  size="small"
                  className={classes.originChip}
                  style={{
                    backgroundColor: isSystem ? "#1976d2" : "#9e9e9e",
                    color: "white",
                    marginRight: 8,
                  }}
                />

                <Typography variant="caption" style={{ marginRight: 8, color: "#666" }}>
                  {group.tags.length} etiqueta{group.tags.length !== 1 ? "s" : ""}
                </Typography>

                {/* Kanban switch — único por empresa */}
                <Box
                  className={classes.kanbanSwitch}
                  onClick={e => e.stopPropagation()}
                >
                  <Typography variant="caption" style={{ fontWeight: 600 }}>
                    Kanban
                  </Typography>
                  <Tooltip title={group.kanbanActive ? "Kanban activo — clic para desactivar" : "Activar como Kanban principal"}>
                    <span>
                      <Switch
                        size="small"
                        checked={!!group.kanbanActive}
                        onChange={() => handleToggleKanban(group)}
                        color="primary"
                        disabled={!isAdmin}
                      />
                    </span>
                  </Tooltip>
                </Box>

                {/* Group actions (manual groups only) */}
                {!isSystem && isAdmin && (
                  <Box className={classes.groupActions} onClick={e => e.stopPropagation()}>
                    {group.name !== "General" && (
                      <Tooltip title="Renombrar grupo">
                        <IconButton
                          size="small"
                          onClick={() => { setRenamingGroup(group); setRenameValue(group.name); }}
                        >
                          <EditIcon fontSize="small" style={{ color: "#1976d2" }} />
                        </IconButton>
                      </Tooltip>
                    )}
                    {group.name !== "General" && (
                      <Tooltip title="Eliminar grupo">
                        <IconButton
                          size="small"
                          onClick={() => { setGroupConfirmOpen(true); setDeletingGroup(group); }}
                        >
                          <DeleteOutlineIcon fontSize="small" style={{ color: "#f44336" }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                )}
              </Box>

              {/* Group content */}
              <Collapse in={isOpen}>
                {visibleTags.length === 0 && !searchParam ? (
                  <Box p={2} textAlign="center">
                    <Typography variant="caption" color="textSecondary">
                      Sin etiquetas en este grupo
                    </Typography>
                  </Box>
                ) : (
                  <Table size="small">
                    <TableHead>
                      <TableRow className={classes.tableHeader}>
                        <TableCell align="center">{i18n.t("tags.table.tagName")}</TableCell>
                        <TableCell align="center">{i18n.t("tags.table.taggedTickets")}</TableCell>
                        <TableCell align="center">{i18n.t("tags.table.kanban")}</TableCell>
                        <TableCell align="center">{i18n.t("tags.table.actions")}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TagsTableBody
                      tags={visibleTags}
                      isSystem={isSystem}
                      groupId={group.id}
                      classes={classes}
                      onDragEnd={handleColumnDragEnd}
                      onRenameColumn={handleRenameColumn}
                      onToggleColumnVisible={handleToggleColumnVisible}
                      onEditTag={handleEditTag}
                      onAskDeleteTag={(tag) => { setTagConfirmOpen(true); setDeletingTag(tag); }}
                    />
                  </Table>
                )}

                {/* Agregar etiqueta al grupo (solo grupos manuales) */}
                {!isSystem && isAdmin && (
                  <Box>
                    <Button
                      className={classes.addTagBtn}
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => handleOpenTagModal(group.id)}
                      color="primary"
                    >
                      Agregar etiqueta
                    </Button>
                  </Box>
                )}
              </Collapse>
            </Box>
          );
        })}
      </Paper>
    </MainContainer>
  );
};

export default Tags;
